import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useFormatter } from "next-intl";

import myaccount from "~/components/common/myaccount.module.scss";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import useGiftcard from "~/hooks/use-giftcard";
import CrossCountry from "~/types/cross-country";
import imageUtils from "~/utils/image-utils";
import intlUtils from "~/utils/intl-utils";
import productUtils from "~/utils/product-utils";

import Icon from "../../common/icon";
import LinkWrapper from "../../common/link-wrapper";
import styles from "./purchases.module.scss";

type Props = {
  order: EVA.Core.OrderDto;
  returns?: boolean;
  webshops?: EVA.Core.GetWebshopsResponse;
};

export default function PurchasesItem(props: Props) {
  const t = useTranslations();
  const { isAuthenticated } = useAuthContext();
  const formatter = useFormatter();
  const breakpoint = useBreakpoint();
  const priceFormatter = intlUtils.usePriceFormatter();
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);
  const listOrderItemImages = hooks.useCallService({ service: Core.GetProducts });

  const order = props.order;

  const filteredLines = order.Lines.filter(
    (el) => !!el.ProductID && ((el.IsCancelled && el.FulfillmentStatus === 800) || !el.IsCancelled)
  );

  const cancelledLines = filteredLines.filter((line) => line.IsCancelled);

  const { data } = useQuery({
    queryKey: ["getOrdersImages", order.ID],
    staleTime: Infinity,
    queryFn: async () => {
      let prodIds = filteredLines.map((el) => el.ProductID!);

      if (prodIds.length > 5) {
        prodIds = prodIds.slice(0, 5);
      }

      const imagesObj = await listOrderItemImages({
        ProductIDs: [...prodIds],
        IncludedFields: ["primary_image", "display_value"],
      });

      let composedImagesList = filteredLines.map((el) => ({
        ID: el.ID,
        product_id: el.ProductID,
      })) as CrossCountry.ProductWithImageOnly[];

      if (imagesObj) {
        let imageListByProductId = Object.values(imagesObj.Products);
        composedImagesList = composedImagesList.map((el) => {
          const currentImage = imageListByProductId.find((image) => image.product_id == el.product_id);
          return { ...el, ...currentImage };
        });

        return composedImagesList;
      }

      return null;
    },
    enabled: !!(evaEndpointUrl && isAuthenticated),
  });

  const digitalGiftCardsQuery = useGiftcard().prepareGetDigitalGiftcardOptionsQuery(order || void 0);

  return (
    <LinkWrapper
      href={`${props.returns ? "returns" : "purchases"}/${order.ID}`}
      className={classNames(myaccount.myaccountCard, styles.purchasesItem)}
    >
      <div className={styles.purchasesItemHeader}>
        <h3>
          {formatter.dateTime(new Date(order.PlacementDate || order.CreationTime), { dateStyle: "full" })} /{" "}
          {t(productUtils.getOrderType(props.order, props.webshops))}
        </h3>
        <Icon name={"chevron-right"} aria-hidden width={16} height={16} className={styles.paymentCardIcon} />
      </div>
      <div className={styles.purchasesItemContent}>
        <div className={styles.purchasesItemProperties}>
          <p>
            {props.returns
              ? t("generic.myaccount.purchases.returnId", { number: order.ID })
              : t("generic.myaccount.purchases.orderId", { number: order.ID })}
          </p>
          {t.rich("generic.myaccount.purchases.orderPriceAndItems", {
            p: (chunks) => (
              <p>
                {order.TotalItems + (cancelledLines || []).length}
                {chunks}
              </p>
            ),
            price: (chunks) => (
              <span>{priceFormatter.currency(Math.abs(order.TotalAmountInTax), { currency: order.CurrencyID })}</span>
            ),
          })}
          {/* visible on desktop */}
          <div className={styles.purchasesItemStatusWrapperDesk}>
            {t.rich("generic.orderStatus", {
              status: (chunks) => <span>{t(productUtils.getOrderStatus(order))}</span>,
              p: (chunks) => <p>{chunks}</p>,
            })}
            {/* <LinkWrapper href="#">{t("generic.track")}</LinkWrapper> */}
          </div>
        </div>
        <div className={styles.purchasesItemImages}>
          {data?.map((el, i) => {
            const digitalBg = digitalGiftCardsQuery.data?.find((card) => card.Identifiers.OrderLineID === el.ID)?.Data
              .Theme.ImageUrl;
            return (
              <Image
                key={i}
                src={digitalBg || el?.primary_image?.url || imageUtils.transparentPixel}
                alt={el.display_value || ""}
                width={breakpoint == "desktop" ? 80 : 64}
                height={breakpoint == "desktop" ? 80 : 64}
              />
            );
          })}
        </div>
        {/* visible on mobile */}
        <div className={styles.purchasesItemStatusWrapper}>
          {t.rich("generic.orderStatus", {
            status: (chunks) => <span>{t(productUtils.getOrderStatus(order))}</span>,
            p: (chunks) => <p>{chunks}</p>,
          })}
          {/* <LinkWrapper href="#">{t("generic.track")}</LinkWrapper> */}
        </div>
      </div>
    </LinkWrapper>
  );
}
